import { Close16, ListChecked16 } from '@carbon/icons-react';
import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import { Dictionary, snakeCase } from 'lodash';
import React, {
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import {
  AiFillApp,
  AiFillConfigCreateTaskFor,
  AiFillConfigFormatAs,
  AiFillConfigGenerateAs,
  AiFillConfigGroupBy,
  AiFillConfigTextLength,
  AiFillConfigTone,
} from 'api/ai-fill/types';
import { Field } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { Keyword16 } from 'components/atoms/icons/keyword';
import { TextLongParagraph16 } from 'components/atoms/icons/long-paragraph';
import { Loading } from 'components/atoms/loading';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popover as PopoverAtom } from 'components/atoms/popover';
import { Option } from 'components/atoms/select';
import { Tag } from 'components/atoms/tag';
import { useProjectFieldsForAIFillConfig } from 'components/organisms/ai-fill/use-project-fields-for-config';
import {
  getAIFillAppId,
  getAIFillAppName,
} from 'components/organisms/ai-fill/utils';
import { Caption01, Label01, ProductiveHeading07 } from 'components/styles';
import { PrereleaseFeature, useFeature } from 'hooks/use-feature';
import { useGetAiFillApps } from 'queries/ai-fill';
import { getRandomColor } from 'utils/get-random-color';
import { isChildNode } from 'utils/is-child-node';
import { floatUid } from 'utils/uid';

const Popover = styled(PopoverAtom)``;
const Header = styled.div`
  padding: var(--spacing-03);
  background-color: var(--gray-10);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Title = styled.div`
  font-size: var(--font-size-lg);
  font-weight: var(--heading-8-weight);
  padding: var(--spacing-03);
`;
const Item = styled.div<{ $active: boolean }>`
  padding: var(--spacing-03) var(--spacing-05);
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  svg {
    flex-shrink: 0;
  }
  ${({ $active }) =>
    $active
      ? css`
          background-color: var(--gray-10);
        `
      : ''};
  &:hover {
    background-color: var(--gray-10);
  }
`;
const List = styled.div`
  overflow: auto;
  padding: var(--spacing-03) 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-01);
`;
const DetailTitle = styled.div`
  ${ProductiveHeading07};
  padding: var(--spacing-03) 0;
`;
const Label = styled.div`
  ${Label01};
  color: var(--gray-50);
`;
const Value = styled.div`
  ${Caption01};
`;
const Config = styled.div`
  padding: var(--spacing-03) 0;
  border-bottom: 1px solid var(--border-color);
`;
const Configs = styled.div`
  margin-top: var(--spacing-03);
`;
const Detail = styled.div`
  background-color: var(--coolest-gray-10);
  padding: var(--spacing-03) var(--spacing-05);
`;
const Content = styled.div`
  display: flex;
  height: calc(100% - 52px);

  ${List} {
    width: 60%;
  }
  ${Detail} {
    width: 40%;
    max-height: 100%;
    overflow-y: auto;
  }
`;
const Wrapper = styled.div`
  width: 648px;
  height: 320px;
`;
const LoadingWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const getIconForApp = (app: AiFillApp) => {
  switch (app.aiFillAppConfig.generateAs) {
    case AiFillConfigGenerateAs.Keywords:
      return <Keyword16 />;
    case AiFillConfigGenerateAs.ActionItems:
      return <ListChecked16 />;
    case AiFillConfigGenerateAs.Summary:
    default:
      return <TextLongParagraph16 />;
  }
};
const configsOrder = [
  'generateAs',
  'createAs',
  'formatAs',
  'groupBy',
  'tone',
  'textLength',
  'createTasksFor',
  'staticKeywords',
  'selectFieldsToPullKeywordsFrom',
];
const formatValuesAsTag = (values: Option[], maximumLimit = 5) => {
  const valuesInLimit = values.slice(0, maximumLimit);
  const tagsForValues = valuesInLimit.map((option) => (
    <Tag {...option} color={option.color ?? getRandomColor(option.value)}>
      {option.label}
    </Tag>
  ));

  if (values.length > maximumLimit) {
    return tagsForValues.concat(
      <Tag color="gray">
        <FormattedMessage
          id="editor.plus_n_more"
          values={{ count: values.length - maximumLimit }}
        />
      </Tag>,
    );
  }

  return tagsForValues;
};
const formatConfigValue = (
  configName,
  value,
  projectFieldsLookup: Dictionary<Field>,
) => {
  switch (configName) {
    case 'generateAs':
      if (Object.values(AiFillConfigGenerateAs).includes(value)) {
        return (
          <FormattedMessage
            id={`ai_fill.config.generate_as.${snakeCase(value)}`}
          />
        );
      }

      return undefined;
    case 'createAs':
      if (
        [AiFillConfigFormatAs.Tasks, AiFillConfigFormatAs.Checklist].includes(
          value,
        )
      ) {
        return (
          <FormattedMessage
            id={`ai_fill.config.create_as.${snakeCase(value)}`}
          />
        );
      }

      return undefined;
    case 'formatAs':
      if (Object.values(AiFillConfigFormatAs).includes(value)) {
        if (
          [AiFillConfigFormatAs.Tasks, AiFillConfigFormatAs.Checklist].includes(
            value,
          )
        ) {
          return undefined;
        }

        return (
          <FormattedMessage
            id={`ai_fill.config.format_as.${snakeCase(value)}`}
          />
        );
      }

      return undefined;
    case 'groupBy':
      if (Object.values(AiFillConfigGroupBy).includes(value)) {
        return (
          <FormattedMessage
            id={`ai_fill.config.group_by.${snakeCase(value)}`}
          />
        );
      }

      return undefined;
    case 'tone':
      if (Object.values(AiFillConfigTone).includes(value)) {
        return (
          <FormattedMessage id={`ai_fill.config.tone.${snakeCase(value)}`} />
        );
      }

      return undefined;
    case 'textLength':
      if (Object.values(AiFillConfigTextLength).includes(value)) {
        return (
          <FormattedMessage
            id={`ai_fill.config.text_length.${snakeCase(value)}`}
          />
        );
      }

      return undefined;
    case 'createTasksFor':
      if (Object.values(AiFillConfigCreateTaskFor).includes(value)) {
        return (
          <FormattedMessage
            id={`ai_fill.config.create_tasks_for.${snakeCase(value)}`}
          />
        );
      }

      return undefined;
    case 'staticKeywords':
      return value != null
        ? formatValuesAsTag(
            value.map((option) => ({
              label: option,
              value: option,
              color: getRandomColor(option),
            })),
          )
        : undefined;
    case 'selectFieldsToPullKeywordsFrom':
      return value != null
        ? formatValuesAsTag(
            value
              .map((fieldId) => projectFieldsLookup[fieldId])
              .filter(Boolean)
              .reduce(
                (options, field) =>
                  options.concat(
                    field.metaData?.options.filter((option) => option.active),
                  ),
                [],
              ),
          )
        : undefined;
    default:
      return undefined;
  }
};

type ConfigList = { configName: string; value: ReactNode | ReactElement }[];
const getAppConfigAsList = (
  app: AiFillApp,
  projectFieldsLookup: Dictionary<Field>,
): ConfigList => {
  return configsOrder
    .map((configName) => {
      const value = formatConfigValue(
        configName,
        configName === 'createAs'
          ? app.aiFillAppConfig.formatAs
          : app.aiFillAppConfig[configName],
        projectFieldsLookup,
      );

      if (value == null) {
        return undefined;
      }

      return {
        configName,
        value,
      };
    })
    .filter(Boolean) as ConfigList;
};

interface Props {
  editor?: Editor;
  insertAsPlaceholder: boolean;
  updateParentElementLocation: (data) => void;
  attachTo?: HTMLElement | null;
}
const BaseAIFillSelectorPopover: FC<Props> = ({
  editor,
  insertAsPlaceholder,
  updateParentElementLocation,
  children,
  attachTo,
}) => {
  const { data: aiFillApps, isLoading: aiFillAppsLoading } = useGetAiFillApps();
  const [showPopover, setPopoverVisibility] = useState(false);
  const [query, setQuery] = useState('');
  const { projectFieldsLookupById } = useProjectFieldsForAIFillConfig();
  const filteredApps = useMemo(
    () =>
      (aiFillApps ?? []).filter((app) =>
        getAIFillAppName(app).toLowerCase().includes(query.toLowerCase()),
      ),
    [aiFillApps, query],
  );
  const closePopover = useCallback(() => {
    setPopoverVisibility(false);
  }, []);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const activeItem = filteredApps?.[activeIndex];
  const insertActiveAppIntoEditor = useCallback(() => {
    if (editor == null) {
      return;
    }
    const { model } = editor;
    const marker = editor.model.markers.get('aiFillSelector');
    const end = model.createPositionAt(model.document.selection.focus);
    const start = model.createPositionAt(marker.getStart());
    const range = model.createRange(start, end);

    editor.execute('ai-fill-insert', {
      appId: getAIFillAppId(activeItem),
      asReadOnly: insertAsPlaceholder,
      uniqueId: floatUid(),
      targetRange: range,
    });

    editor.editing.view.focus();
  }, [activeItem, editor, insertAsPlaceholder]);
  const moveFocusTo = useCallback((index: number) => {
    document
      .querySelector(`[data-ai-fill-app-position='${index}']`)
      ?.scrollIntoView({
        block: 'nearest',
      });
  }, []);
  const handleSelectorEvents = useCallback(
    (e, data?) => {
      if (e.name === 'aiFillSelector:render') {
        setPopoverVisibility(true);
        updateParentElementLocation({
          left: data.position.left,
          top: data.position.top,
        });
        setActiveIndex(0);
      } else if (e.name === 'aiFillSelector:remove') {
        closePopover();
      } else if (e.name === 'aiFillSelector:query') {
        setQuery(data);
        setActiveIndex(0);
      } else if (e.name === 'aiFillSelector:selectPrevious') {
        setActiveIndex((previous) => {
          const next = previous === 0 ? filteredApps.length - 1 : previous - 1;

          moveFocusTo(next);

          return next;
        });
      } else if (e.name === 'aiFillSelector:selectNext') {
        setActiveIndex((previous) => {
          const next = (previous + 1) % filteredApps.length;

          moveFocusTo(next);

          return next;
        });
      } else if (e.name === 'aiFillSelector:executeSelected') {
        insertActiveAppIntoEditor();
      }
    },
    [
      closePopover,
      filteredApps.length,
      moveFocusTo,
      insertActiveAppIntoEditor,
      updateParentElementLocation,
    ],
  );

  useEffect(() => {
    if (editor) {
      editor.on('aiFillSelector', handleSelectorEvents);
    }

    return () => {
      if (editor) {
        editor.off('aiFillSelector', handleSelectorEvents);
      }
    };
  }, [editor, handleSelectorEvents]);
  const isChild = isChildNode([
    'ai-fill-selector-popup',
    'ck-editor__editable',
  ]);
  const clearAndClose = useCallback(() => {
    closePopover();
    const { model } = editor;

    editor.model.change((writer: any) => {
      const marker = model.markers.get('aiFillSelector');

      if (!marker) {
        return;
      }

      // Create a range on matched text.
      const end = model.createPositionAt(model.document.selection.focus);
      const start = model.createPositionAt(marker.getStart());
      const range = model.createRange(start, end);
      const selection = writer.createSelection(range);

      model.deleteContent(selection);
    });
  }, [closePopover, editor]);

  return ReactDOM.createPortal(
    <OutsideClickHandler allowed={isChild} onOutsideClick={clearAndClose}>
      <Popover
        visible={showPopover}
        className="ai-fill-selector-popup"
        placement="bottomRight"
        getPopupContainer={attachTo ? () => attachTo as HTMLElement : undefined}
        content={
          aiFillAppsLoading ? (
            <LoadingWrapper>
              <Loading />
            </LoadingWrapper>
          ) : (
            <Wrapper>
              <Header>
                <Title>
                  <FormattedMessage id="ai_fill.selector.title" />
                </Title>
                <Button
                  icon={<Close16 />}
                  size="small"
                  type="text"
                  onClick={clearAndClose}
                />
              </Header>
              <Content>
                <List>
                  {filteredApps.map((app, index) => (
                    <Item
                      key={getAIFillAppId(app)}
                      $active={
                        getAIFillAppId(app) ===
                        (activeItem && getAIFillAppId(activeItem))
                      }
                      data-ai-fill-app-position={index}
                      onMouseOver={() => setActiveIndex(index)}
                      onClick={insertActiveAppIntoEditor}
                    >
                      {getIconForApp(app)} {getAIFillAppName(app)}
                    </Item>
                  ))}
                </List>
                {activeItem != null ? (
                  <Detail>
                    <DetailTitle>{getAIFillAppName(activeItem)}</DetailTitle>
                    <Configs>
                      {getAppConfigAsList(
                        activeItem,
                        projectFieldsLookupById,
                      ).map((config) => (
                        <Config>
                          <Label>
                            <FormattedMessage
                              id={`ai_fill.config.${snakeCase(
                                config.configName,
                              )}`}
                            />
                          </Label>
                          <Value>{config.value}</Value>
                        </Config>
                      ))}
                    </Configs>
                  </Detail>
                ) : null}
              </Content>
            </Wrapper>
          )
        }
      >
        {children}
      </Popover>
    </OutsideClickHandler>,
    document.body,
  );
};

export const AIFillSelectorPopover: FC<Props> = ({ children, ...props }) => {
  const meetingsAIEnabled = useFeature(PrereleaseFeature.MeetingsAI);

  return meetingsAIEnabled ? (
    <BaseAIFillSelectorPopover {...props}>{children}</BaseAIFillSelectorPopover>
  ) : (
    <></>
  );
};
